import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { ConnectedPosition } from '@angular/cdk/overlay'
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms'
import dayjs, { Dayjs } from 'dayjs'
import { TranslateService } from '@ngx-translate/core'
import { MatSnackBar } from '@angular/material/snack-bar'
import { DaterangepickerComponent } from 'ngx-daterangepicker-material'

import { DatepickerRange } from '@shared/models/datepicker-range.model'
import { AuthService } from '@app/modules/auth/auth.service'
import { dateFormats } from '@core/constants/dateFormats'

export type DatepickerRangeChangeCustom = { startDate: Dayjs; endDate: Dayjs } | Dayjs

interface SavedTimeRange {
  startHour: number
  startMinute: number
  endHour: number
  endMinute: number
}

/**
 * Custom datepicker using inline ngx-daterangepicker-material + CDK overlay popup.
 *
 * Key differences vs lin-datepicker-field:
 * - Custom popup footer with Save/Recall time buttons inside the modal.
 * - Explicit Apply / Cancel buttons: changes are committed only on Apply.
 * - Save/Recall time persisted to localStorage; predefined ranges respect saved time.
 */
@Component({
  selector: 'lin-datepicker-field-custom',
  templateUrl: './datepicker-field-custom.component.html',
  styleUrls: ['./datepicker-field-custom.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatepickerFieldCustomComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DatepickerFieldCustomComponent implements ControlValueAccessor, OnInit {
  /**
   * Date/time currently being selected inside the popup (source of truth for Recall/Save/Apply).
   * Updated only by: onDatesUpdated (user interaction) and recallTimeRange/openDatepicker.
   * NOT used directly for [startDate]/[endDate] to avoid a feedback loop.
   */
  selected: { startDate: Dayjs | null; endDate: Dayjs | null } = { startDate: null, endDate: null }

  /**
   * Value pushed into the picker's [startDate]/[endDate] inputs.
   * Only changes when we intentionally want to override the picker from code (open popup, recall).
   * Kept separate from `selected` to prevent a loop: onDatesUpdated → selected changes →
   * Angular re-evaluates [startDate] → library fires datesUpdated again → overwrites selected.
   */
  pickerInitDate: { startDate: Dayjs | null; endDate: Dayjs | null } = { startDate: null, endDate: null }

  /** The last value committed to the parent form (on Apply). */
  private committed: { startDate: Dayjs | null; endDate: Dayjs | null } = { startDate: null, endDate: null }

  isOpen = false
  locale: { applyLabel: string; format: string }
  maxDate: string
  ranges: { [k: string]: Dayjs[] } | null = null
  savedTimeRangeLabel: string | null = null
  isTimeRangeForEachDay = false

  /** Popup position: prefer below the input, fall back to above if there is not enough space. */
  readonly overlayPositions: ConnectedPosition[] = [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ]

  private readonly TIME_RANGE_KEY = 'linxio_saved_time_range'
  private readonly EACH_DAY_KEY = 'linxio_time_range_each_day'

  @Input() isRange = true
  @Input() isPlaceholderShown = false
  @Input() isWithTimePicker = false
  @Input() isNoMaxDate = false
  @Input() placeholder: string
  @Input() minDate: string
  @Input() isDisabled = false
  @Input() isError = false
  @Input() showEachDayCheckbox = false
  @Output() datePickerRangeChange = new EventEmitter<DatepickerRangeChangeCustom>()

  @ViewChild(DaterangepickerComponent) picker: DaterangepickerComponent
  @ViewChild('popupContainer') private popupContainerRef?: ElementRef

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: DatepickerRangeChangeCustom) => void = () => {}
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {}

  @HostListener('window:keydown.escape')
  onEscape(): void {
    if (this.isOpen) this.cancel()
  }

  /**
   * Close the popup when clicking outside.
   * cdkConnectedOverlayHasBackdrop is not used because a transparent backdrop still blocks mouse events,
   * making buttons inside the popup unclickable.
   */
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isOpen) return
    const target = event.target as HTMLElement
    // Click on the host element (trigger input) → ignore (openDatepicker already handles it)
    if ((this.elementRef.nativeElement as HTMLElement).contains(target)) return
    // Click inside the popup → ignore
    if ((this.popupContainerRef?.nativeElement as HTMLElement | undefined)?.contains(target)) return
    this.cancel()
  }

  get dateFormat(): string {
    const fmt = this.authService.getCurrentUserDateFormat() || dateFormats.date
    return this.isWithTimePicker ? `${fmt} HH:mm` : fmt
  }

  /** Text shown in the trigger input (reflects committed value). */
  get displayLabel(): string {
    const { startDate, endDate } = this.committed
    if (!startDate) {
      return this.isPlaceholderShown ? this.placeholder || (this.translateService.instant('MAIN.CHOOSE_DATE') as string) : ''
    }
    const start = dayjs(startDate).format(this.dateFormat)
    if (!this.isRange) return start
    const end = endDate ? dayjs(endDate).format(this.dateFormat) : start
    return `${start} - ${end}`
  }

  constructor(
    private readonly authService: AuthService,
    private readonly translateService: TranslateService,
    private readonly cdr: ChangeDetectorRef,
    private readonly snackBar: MatSnackBar,
    private readonly elementRef: ElementRef,
  ) {}

  ngOnInit(): void {
    this.initSettings()
    this.loadSavedTimeLabel()
    this.loadEachDayState()
  }

  // ---------------------------------------------------------------------------
  // Popup lifecycle
  // ---------------------------------------------------------------------------

  openDatepicker(event: MouseEvent): void {
    /**
     * stopPropagation: prevents this click from bubbling up to the document.
     * Without it, the document:click HostListener would fire right after isOpen = true
     * and call cancel() → the popup closes immediately (race condition).
     */
    event.stopPropagation()
    if (this.isDisabled) return
    // Toggle: clicking the input again while the popup is open → close it
    if (this.isOpen) {
      this.cancel()
      return
    }
    // Reset both selected and pickerInitDate to committed when opening the popup
    this.selected = { startDate: this.committed.startDate, endDate: this.committed.endDate }
    this.pickerInitDate = { startDate: this.committed.startDate, endDate: this.committed.endDate }
    this.maxDate = !this.isNoMaxDate ? dayjs().tz().endOf('day').format('YYYY/MM/DD HH:mm') : ''
    // Rebuild ranges to apply the latest saved time
    this.initRanges()
    this.isOpen = true
    // markForCheck instead of detectChanges: avoids synchronous cascading change detection
    // which causes CDK overlay to attach multiple times → multiple popups.
    // Zone.js will trigger CD after this event handler returns.
    this.cdr.markForCheck()
  }

  /**
   * Fired by ngx-daterangepicker-material via (datesUpdated) when the user
   * changes a date OR time. We update `selected` but do NOT commit yet.
   */
  onDatesUpdated(event: { startDate: Dayjs; endDate: Dayjs }): void {
    if (!event?.startDate) return
    // Only update `selected` – do NOT update pickerInitDate.
    // Updating pickerInitDate would cause Angular to re-evaluate [startDate]/[endDate],
    // making the library fire datesUpdated again → overwriting selected with the old value (feedback loop).
    this.selected = {
      startDate: dayjs(event.startDate.valueOf()),
      endDate: event.endDate ? dayjs(event.endDate.valueOf()) : dayjs(event.startDate.valueOf()),
    }
  }

  /** Commit the currently selected range to the parent form and close the popup. */
  apply(): void {
    // Prefer the picker's live state (includes time changes not yet captured by datesUpdated)
    const pickerState = this.getPickerState()
    const startDate = pickerState?.startDate ?? this.selected.startDate
    const endDate = pickerState?.endDate ?? this.selected.endDate
    if (!startDate) return
    this.committed = { startDate, endDate: endDate ?? startDate }
    const value: DatepickerRangeChangeCustom = this.isRange
      ? { startDate: this.committed.startDate, endDate: this.committed.endDate }
      : this.committed.startDate
    this.onChange(value)
    this.onTouched()
    this.datePickerRangeChange.emit(value)
    this.isOpen = false
    this.cdr.markForCheck()
  }

  /** Discard changes and close popup without emitting. */
  cancel(): void {
    this.selected = { startDate: this.committed.startDate, endDate: this.committed.endDate }
    this.isOpen = false
    this.cdr.markForCheck()
  }

  // ---------------------------------------------------------------------------
  // Save / Recall time
  // ---------------------------------------------------------------------------

  saveTimeRange(): void {
    // Read directly from the picker to get the current time value.
    // (datesUpdated) does not fire when only time selects change with autoApply=false.
    const pickerState = this.getPickerState()
    const startDate = pickerState?.startDate ?? this.selected.startDate
    const endDate = pickerState?.endDate ?? this.selected.endDate
    if (!startDate || !endDate) return
    const config: SavedTimeRange = {
      startHour: dayjs(startDate).hour(),
      startMinute: dayjs(startDate).minute(),
      endHour: dayjs(endDate).hour(),
      endMinute: dayjs(endDate).minute(),
    }
    localStorage.setItem(this.TIME_RANGE_KEY, JSON.stringify(config))
    this.loadSavedTimeLabel()
    this.initRanges()
    const label = `${this.padTime(config.startHour)}:${this.padTime(config.startMinute)} – ${this.padTime(config.endHour)}:${this.padTime(
      config.endMinute,
    )}`
    const message = this.translateService.instant('REPORTS.TIME_RANGE_CONFIG.SAVE_SUCCESS', { time: label }) as string
    this.snackBar.open(message, '', { duration: 3000 })
    this.cdr.markForCheck()
  }

  recallTimeRange(): void {
    const config = this.loadSavedTimeConfig()
    if (!config) return
    // Prefer the picker's live state to get the current date (includes dates not yet through datesUpdated)
    const pickerState = this.getPickerState()
    const baseStart = pickerState?.startDate ?? this.selected.startDate
    const baseEnd = pickerState?.endDate ?? this.selected.endDate
    if (!baseStart || !baseEnd) return
    this.selected = {
      startDate: dayjs(baseStart).hour(config.startHour).minute(config.startMinute).second(0),
      endDate: dayjs(baseEnd).hour(config.endHour).minute(config.endMinute).second(0),
    }
    // Sync pickerInitDate so Angular pushes the new value into [startDate]/[endDate]
    // → library updates time selects visually (this is the only time we need to override the picker).
    this.pickerInitDate = { startDate: this.selected.startDate, endDate: this.selected.endDate }
    this.cdr.markForCheck()
  }

  // ---------------------------------------------------------------------------
  // ControlValueAccessor
  // ---------------------------------------------------------------------------

  writeValue(obj: DatepickerRange<Dayjs> | Dayjs | null): void {
    if (!obj) {
      this.committed = { startDate: null, endDate: null }
      this.selected = { startDate: null, endDate: null }
      this.pickerInitDate = { startDate: null, endDate: null }
      this.cdr.markForCheck()
      return
    }
    let startDate: Dayjs | null = null
    let endDate: Dayjs | null = null
    if ('startDate' in (obj as DatepickerRange<Dayjs>)) {
      const range = obj as DatepickerRange<Dayjs>
      startDate = range.startDate ? dayjs(range.startDate) : null
      endDate = range.endDate ? dayjs(range.endDate) : null
    } else {
      startDate = dayjs(obj as Dayjs)
      endDate = dayjs(obj as Dayjs)
    }
    // Auto-apply saved time only on initial load (committed is still null).
    // Subsequent writeValue calls come from parent patchValue after Apply → respect user's choice.
    if (this.isWithTimePicker && startDate && endDate && !this.committed.startDate) {
      const saved = this.loadSavedTimeConfig()
      if (saved) {
        startDate = startDate.hour(saved.startHour).minute(saved.startMinute).second(0)
        endDate = endDate.hour(saved.endHour).minute(saved.endMinute).second(0)
      }
    }
    this.committed = { startDate, endDate }
    this.selected = { startDate, endDate }
    this.pickerInitDate = { startDate, endDate }
    this.cdr.markForCheck()
  }

  registerOnChange(fn: (value: DatepickerRangeChangeCustom) => void): void {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled
    this.cdr.markForCheck()
  }

  // ---------------------------------------------------------------------------
  // Private helpers
  // ---------------------------------------------------------------------------

  /**
   * Read the live state from the picker component (includes time changes not yet captured by datesUpdated).
   * With autoApply=false, the library does not fire datesUpdated when only time selects change.
   */
  private getPickerState(): { startDate: Dayjs; endDate: Dayjs } | null {
    if (!this.picker) return null
    const { startDate, endDate } = this.picker
    if (!startDate) return null
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    // dayjs.utc() preserves the UTC hour from the picker's internal ESM dates so that
    // .hour() returns the display hour and downstream .tz(tz, true) works without error.
    return { startDate: dayjs.utc((startDate as any).valueOf()), endDate: dayjs.utc(((endDate ?? startDate) as any).valueOf()) }
  }

  private initSettings(): void {
    const applyLabel = this.translateService.instant('MAIN.DONE') as string
    this.locale = { applyLabel, format: this.dateFormat }
    this.maxDate = !this.isNoMaxDate ? dayjs().tz().endOf('day').format('YYYY/MM/DD HH:mm') : ''
    if (!this.placeholder && this.isPlaceholderShown) {
      this.placeholder = this.translateService.instant('MAIN.CHOOSE_DATE') as string
    }
    this.initRanges()
  }

  private initRanges(): void {
    if (!this.isRange) return
    const now = dayjs().tz()
    const saved = this.loadSavedTimeConfig()

    const applyTime = (date: Dayjs, isEnd: boolean): Dayjs => {
      if (saved) {
        return isEnd
          ? date.hour(saved.endHour).minute(saved.endMinute).second(0)
          : date.hour(saved.startHour).minute(saved.startMinute).second(0)
      }
      return isEnd ? date.endOf('day') : date.startOf('day')
    }

    this.ranges = {
      [this.translateService.instant('DATE_RANGES.TODAY')]: [applyTime(now, false), applyTime(now, true)],
      [this.translateService.instant('DATE_RANGES.YESTERDAY')]: [
        applyTime(now.subtract(1, 'days'), false),
        applyTime(now.subtract(1, 'days'), true),
      ],
      [this.translateService.instant('DATE_RANGES.LAST_7_DAYS')]: [applyTime(now.subtract(6, 'days'), false), applyTime(now, true)],
      [this.translateService.instant('DATE_RANGES.LAST_30_DAYS')]: [applyTime(now.subtract(29, 'days'), false), applyTime(now, true)],
      [this.translateService.instant('DATE_RANGES.THIS_MONTH')]: [
        applyTime(now.startOf('month'), false),
        applyTime(now.endOf('month'), true),
      ],
      [this.translateService.instant('DATE_RANGES.LAST_MONTH')]: [
        applyTime(now.subtract(1, 'month').startOf('month'), false),
        applyTime(now.subtract(1, 'month').endOf('month'), true),
      ],
    }
  }

  private padTime(n: number): string {
    return n.toString().padStart(2, '0')
  }

  private loadSavedTimeConfig(): SavedTimeRange | null {
    const raw = localStorage.getItem(this.TIME_RANGE_KEY)
    if (!raw) return null
    try {
      return JSON.parse(raw) as SavedTimeRange
    } catch {
      localStorage.removeItem(this.TIME_RANGE_KEY)
      return null
    }
  }

  private loadEachDayState(): void {
    this.isTimeRangeForEachDay = localStorage.getItem(this.EACH_DAY_KEY) === 'true'
  }

  onEachDayToggle(): void {
    localStorage.setItem(this.EACH_DAY_KEY, String(this.isTimeRangeForEachDay))
  }

  private loadSavedTimeLabel(): void {
    const config = this.loadSavedTimeConfig()
    if (!config) {
      this.savedTimeRangeLabel = null
      return
    }
    this.savedTimeRangeLabel = `${this.padTime(config.startHour)}:${this.padTime(config.startMinute)} – ${this.padTime(
      config.endHour,
    )}:${this.padTime(config.endMinute)}`
  }
}
